import { z } from 'zod';
/**
 * credify-platform#306 (E1) - the signup module creates a new practice (organization) and its
 * first admin at go-live, server to server: `POST /api/v1/orgs-machine/provision`, behind the
 * `signup` service principal (scope `orgs:provision`). See apps/api/src/modules/orgs-machine.
 */
/**
 * The practice time zones the EHR offers (Data & settings > Practice info > Time zone). The org
 * stores the label; `ianaZone()` in the API reads the IANA name back out of it.
 */
export const PRACTICE_TIMEZONES = [
    'Pacific — America/Los_Angeles',
    'Mountain — America/Denver',
    'Central — America/Chicago',
    'Eastern — America/New_York',
];
/** A label from PRACTICE_TIMEZONES, or its bare IANA name ("America/Denver"), to the label. */
export function practiceTimezoneLabel(value) {
    const v = value.trim();
    return PRACTICE_TIMEZONES.find((label) => label === v || label.endsWith(`— ${v}`)) ?? null;
}
/** Lower-case letters, digits and single hyphens; starts and ends with a letter or digit. */
export const ORG_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const personName = z.string().trim().min(1).max(80);
export const orgProvisionSchema = z
    .object({
    /** signup's application row id: the idempotency anchor (a repeat returns the same ids). */
    applicationId: z.string().uuid(),
    org: z
        .object({
        name: z.string().trim().min(2).max(120),
        slug: z
            .string()
            .trim()
            .min(3)
            .max(60)
            .regex(ORG_SLUG_RE, 'Use lower-case letters, digits and single hyphens'),
        timezone: z
            .string()
            .max(80)
            .transform((v, ctx) => {
            const label = practiceTimezoneLabel(v);
            if (!label) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Use one of: ${PRACTICE_TIMEZONES.join('; ')}`,
                });
                return z.NEVER;
            }
            return label;
        }),
    })
        .strict(),
    admin: z
        .object({
        email: z.string().trim().toLowerCase().email().max(254),
        firstName: personName,
        lastName: personName,
        /** The first user is always the practice admin; other roles come later from Users & Profile. */
        role: z.literal('admin').default('admin'),
    })
        .strict(),
    /**
     * Also create the org's `signup-svc+<orgId>@credifyfast.com` admin (the portal's
     * `portal-svc+` pattern), so go-live can call today's user-shaped routes (DESIGN Q3).
     */
    serviceUser: z.boolean().default(false),
})
    .strict();
/** The per-org service user signup signs in as on go-live's user-shaped calls. */
export const signupServiceUserEmail = (orgId) => `signup-svc+${orgId}@credifyfast.com`;
//# sourceMappingURL=orgProvision.js.map